import { cn } from "@tellescope/ui";
import { LogOut, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ChildPage, Page } from "../../shared/schema";

interface PortalSidebarProps {
  pages: Page[];
  currentPage: Page | ChildPage | null;
  onNavigate: (pageId: string) => void;
  portalName?: string;
  onLogout?: () => void;
  /** Organization logo URL - displayed instead of text when available */
  logoURL?: string;
  /** Organization name - used as fallback when no logo is available */
  organizationName?: string;
  /**
   * When true, renders navigation items as <Link> components for native link behavior
   * When false, renders as buttons that call onNavigate callback.
   */
  useLinks?: boolean;
  /** The slug of the first page in the full schema (used to resolve the "/" URL correctly when pages may be filtered) */
  rootSlug?: string;
}

export const PortalSidebar: React.FC<PortalSidebarProps> = ({
  pages,
  currentPage,
  onNavigate,
  portalName,
  onLogout,
  logoURL,
  organizationName,
  useLinks = false,
  rootSlug,
}) => {
  const { t } = useTranslation();

  // Tracks which single top-level page's sub-pages are expanded (collapsed by default,
  // except the parent of the initially-active sub-page so a deep link lands expanded).
  const [expandedPageId, setExpandedPageId] = useState<string | null>(() => {
    const activeParent = pages.find((p) => p.pages.some((c) => c.id === currentPage?.id));
    return activeParent?.id ?? null;
  });

  // Use rootSlug to correctly resolve "/" URL even when pages may be filtered by visibility
  const firstPageSlug = rootSlug ?? pages[0]?.slug;

  // Get the URL path for a top-level page
  const getPagePath = (page: Page): string => {
    return page.slug === firstPageSlug ? "/" : `/${page.slug}`;
  };

  // Get the URL path for a sub-page nested under a top-level page
  const getChildPagePath = (page: Page, child: ChildPage): string => {
    const parentPath = getPagePath(page);
    return `${parentPath === "/" ? "" : parentPath}/${child.slug}`;
  };

  // Display name: use organization name if available, fall back to portal name, then default
  const displayName = organizationName || portalName || t("My Portal");

  return (
    <aside
      className="w-64 h-full shrink-0 border-r bg-primary/5 flex flex-col"
      style={{ backgroundColor: "var(--portal-header-color, var(--background))" }}
    >
      {/* Logo / Organization Header */}
      <div className="px-6 py-8">
        {logoURL ? (
          <img src={logoURL} alt={displayName} className="h-10 max-w-[180px] object-contain" />
        ) : (
          <span className="text-lg font-semibold text-foreground">{displayName}</span>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-0">
          {pages.map((page) => {
            const isActive = currentPage?.id === page.id;
            const hasChildren = page.pages.length > 0;
            const isExpanded = expandedPageId === page.id;
            const navItemClasses = cn(
              "flex-1 text-left py-3 text-sm transition-colors block",
              isActive ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground"
            );

            const expandOnNavigate = () => {
              if (hasChildren) setExpandedPageId(page.id);
            };

            return (
              <div key={page.id}>
                <div className="flex items-center gap-1">
                  {useLinks ? (
                    <Link to={getPagePath(page)} onClick={expandOnNavigate} className={navItemClasses}>
                      {page.title}
                    </Link>
                  ) : (
                    <button
                      onClick={() => {
                        onNavigate(page.id);
                        expandOnNavigate();
                      }}
                      className={navItemClasses}
                    >
                      {page.title}
                    </button>
                  )}

                  {hasChildren && (
                    <button
                      type="button"
                      onClick={() => setExpandedPageId(isExpanded ? null : page.id)}
                      className={cn(
                        "p-1 rounded hover:bg-muted-foreground/10 flex-shrink-0",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}
                      title={isExpanded ? t("Collapse sub-pages") : t("Expand sub-pages")}
                    >
                      <ChevronDown className={cn("h-4 w-4 transition-transform", isExpanded ? "rotate-180" : "")} />
                    </button>
                  )}
                </div>

                {hasChildren && isExpanded && (
                  <div className="ml-4 border-l border-l-border pl-3">
                    {page.pages.map((child) => {
                      const isChildActive = currentPage?.id === child.id;
                      const childNavItemClasses = cn(
                        "w-full text-left py-2 text-sm transition-colors block",
                        isChildActive ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground"
                      );

                      return useLinks ? (
                        <Link key={child.id} to={getChildPagePath(page, child)} className={childNavItemClasses}>
                          {child.title}
                        </Link>
                      ) : (
                        <button key={child.id} onClick={() => onNavigate(child.id)} className={childNavItemClasses}>
                          {child.title}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* Logout Section */}
      {onLogout && (
        <div className="px-6 py-4 border-t">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 py-3 text-sm text-muted-foreground hover:text-destructive transition-colors"
          >
            <LogOut className="h-4 w-4" />
            {t("Log out")}
          </button>
        </div>
      )}
    </aside>
  );
};
